// Typed wrappers for go-pim product catalogs — the per-merchant grouping a
// product sits in (go-pim migration 0004).
//
// A catalog belongs to exactly ONE company, and a product to at most one
// catalog. A product with no catalog is UNGROUPED, which is a permanent and
// legitimate state rather than a transitional one — the UI shows it as its own
// bucket beside the real catalogs.
//
// No money here on purpose: a catalog carries no rate, currency or tax scheme.
// Pricing stays entirely on the product, and nothing in go-pim's document
// generation reads catalogs, so renaming or deleting one can never change what
// an already-issued receipt or invoice said.
import { apiClient } from "./client";

export interface Catalog {
  id: string;
  company_id: string;
  name: string;
  description: string;
  sort_order: number;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Fields accepted by POST /v1/companies/:id/catalogs and PUT /v1/catalogs/:id.
 *
 * Note the two routes do NOT treat an omitted field the same way, and the API
 * enforces this rather than guessing:
 *
 *   POST  omitting a field takes its default (active TRUE). Nothing exists yet,
 *         so nothing can be lost.
 *   PUT   omitting description, sort_order or active is a 400 NAMING the field.
 *         That route is a full-row replacement, so a partial body would blank
 *         whatever it left out.
 *
 * So always send the complete object on update — which costs nothing, because
 * responses carry every key (go-pim deliberately uses no `omitempty` on the
 * catalog DTO) and anything fetched can be sent straight back.
 */
export interface CatalogWritePayload {
  name: string;
  description: string;
  sort_order: number;
  active: boolean;
}

/** listCatalogs — GET /v1/companies/:companyID/catalogs (display order). */
export async function listCatalogs(
  companyId: string,
  params: { active_only?: boolean } = {},
): Promise<Catalog[]> {
  const resp = await apiClient.get<{ catalogs: Catalog[] }>(
    `/v1/companies/${companyId}/catalogs`,
    { params },
  );
  return resp.data.catalogs ?? [];
}

/** createCatalog — POST /v1/companies/:companyID/catalogs */
export async function createCatalog(
  companyId: string,
  payload: CatalogWritePayload,
): Promise<Catalog> {
  const resp = await apiClient.post<Catalog>(
    `/v1/companies/${companyId}/catalogs`,
    payload,
  );
  return resp.data;
}

/** updateCatalog — PUT /v1/catalogs/:id (full replacement — send every field). */
export async function updateCatalog(
  id: string,
  payload: CatalogWritePayload,
): Promise<Catalog> {
  const resp = await apiClient.put<Catalog>(`/v1/catalogs/${id}`, payload);
  return resp.data;
}

/**
 * deleteCatalog — DELETE /v1/catalogs/:id
 *
 * A catalog that still has products is REFUSED with a 400 naming the member
 * count; deleting a grouping never deletes or ungroups the priced items inside
 * it. Surface that message to the operator rather than swallowing it — it tells
 * them exactly what is in the way. (go-pim enforces this twice: the count, and
 * a RESTRICT foreign key that refuses regardless.)
 */
export async function deleteCatalog(id: string): Promise<void> {
  await apiClient.delete(`/v1/catalogs/${id}`);
}

/**
 * moveProductToCatalog — PUT /v1/products/:id/catalog
 *
 * `catalogId: ""` UNGROUPS the product, which is a real destination rather than
 * a missing value.
 *
 * This is its own endpoint, not a field on the product update, and that is
 * deliberate on the server side: PUT /v1/products/:id is a full-row replace, so
 * a catalog_id there would be silently blanked by any caller that did not send
 * it. Never try to move a product by including catalog_id in an update payload
 * — the API ignores it there by design.
 */
export async function moveProductToCatalog(
  productId: string,
  catalogId: string,
): Promise<void> {
  await apiClient.put(`/v1/products/${productId}/catalog`, {
    catalog_id: catalogId,
  });
}
